import {
  DynamicDrawUsage,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  SphereGeometry,
  Vector3,
} from "three";

import { Frames } from "../../util/frames";

export class BallTrail extends Object3D {
  material = new MeshBasicMaterial({ color: 0xbd9e4d });
  private geom = new SphereGeometry(1, 16, 8);

  private trail: InstancedMesh;
  private dummy = new Mesh();
  private tempM = new Matrix4();
  private tempP = new Vector3();
  // workaround to not show the instance that appears at 0, 0, 0
  // TODO: work out what the issue is here
  private outOfClipY = -10000;
  private maxSize: number;
  private perFrame: number;

  private trailLen: number;

  private ballPosition: {
    curr: Vector3;
    temp: Vector3;
    prev?: Vector3;
  } = {
    curr: new Vector3(),
    temp: new Vector3(),
  };

  private index = 0;

  constructor(trailFramesLen = 100, perFrame = 4, maxSize = 0.15) {
    super();

    this.trailLen = trailFramesLen * perFrame;

    this.trail = new InstancedMesh(this.geom, this.material, this.trailLen);
    this.trail.instanceMatrix.setUsage(DynamicDrawUsage);

    // put init spheres out of clip
    this.dummy.position.set(0, -this.outOfClipY, 0);
    this.dummy.updateMatrix();

    for (let i = 0; i < this.trailLen; i++) {
      this.trail.setMatrixAt(i, this.dummy.matrix);
    }

    this.add(this.trail);

    this.maxSize = maxSize;
    this.perFrame = perFrame;
  }

  update(frames: Frames) {
    for (const frame of [...frames.missed, frames.current]) {
      if (frame == undefined) continue;

      const { ballPosition } = frame;
      if (ballPosition) {
        this.ballPosition.curr.set(
          ballPosition[0],
          ballPosition[1],
          ballPosition[2]
        );

        if (!this.ballPosition.prev) {
          this._update(this.ballPosition.curr);
          this.ballPosition.prev = new Vector3();
        } else {
          for (let a = 0; a < this.perFrame; a++) {
            const alpha = a / this.perFrame;
            this.ballPosition.temp.lerpVectors(
              this.ballPosition.prev,
              this.ballPosition.curr,
              alpha
            );
            this._update(this.ballPosition.temp);
          }
        }

        this.ballPosition.prev.copy(this.ballPosition.curr);
      } else {
        this.ballPosition.prev = undefined;
        this._update(undefined);
      }
    }
  }

  private _update(ballPosition?: Vector3) {
    const prevIndex = (current: number) =>
      current <= 0 ? this.trailLen - 1 : current - 1;

    if (ballPosition) {
      this.dummy.position.copy(ballPosition);
      this.dummy.scale.set(this.maxSize, this.maxSize, this.maxSize);
      this.dummy.updateMatrix();

      this.trail.setMatrixAt(this.index, this.dummy.matrix);
    } else {
      // TODO: do per instance visibility as described here:
      //       https://github.com/mrdoob/three.js/issues/22102#issuecomment-877004180
      this.dummy.position.set(0, this.outOfClipY, 0);
      // this.dummy.geometry.s
      this.dummy.updateMatrix();

      this.trail.setMatrixAt(this.index, this.dummy.matrix);
    }

    for (
      let otherIndex = prevIndex(this.index);
      otherIndex != this.index;
      otherIndex = prevIndex(otherIndex)
    ) {
      this.trail.getMatrixAt(otherIndex, this.tempM);
      this.tempP.setFromMatrixPosition(this.tempM);

      const dist =
        otherIndex > this.index
          ? this.trailLen - otherIndex + this.index
          : this.index - otherIndex;

      // this is just a line f(size, dist) from (0, maxDist) to (maxSize, 0)
      // where the dist is the distance from the current index, or the number of
      // updates the ball at otherIndex has seen since it was the index and
      // got it's position set
      // const scale = this.maxSize;
      const scale = this.maxSize - (this.maxSize / this.trailLen) * dist;

      this.dummy.position.copy(this.tempP);
      this.dummy.scale.set(scale, scale, scale);
      this.dummy.updateMatrix();

      this.trail.setMatrixAt(otherIndex, this.dummy.matrix);
    }

    this.trail.instanceMatrix.needsUpdate = true;

    this.index = (this.index + 1) % this.trail.count;
  }
}
